
import { Component, Emit, Prop, Ref, Vue, Watch } from 'vue-property-decorator'
import { Observer } from 'mobx-vue'
import EditorContent from './content.vue'
import ImageUploader from './uploader.vue'
import draggable from 'vuedraggable'
import type { UploadItemType, UploadSuccessItem } from '@xt/client/utils/uploader/types.d'

type BasicOptions = {
  // 是否展示返回按钮
  showBackButton: boolean
  // 内容是否必填
  contentRequired: boolean
  // 图片是否必填
  imageRequired: boolean
  // 图片个数上传上限
  maxImageSize: number
  // 内容编辑区标题
  editorTitle: string
  // 图片上传区域标题
  uploaderTitle: string
  // 内容区placeholder
  contentPlaceholder: string
}

interface HomewrokOptions extends BasicOptions {
  type: 'homework'
  // 当前编辑区锚定在作业详情上的标题
  descriptionTitle: string
  // 作业描述富文本
  description: string
  // 是否在请求作业任务详情数据
  isLoading: boolean
  // 作业任务id
  taskId: number
}

interface DairyOptions extends BasicOptions {
  type: 'dairy'
  // 课程id
  courseId: number
}

interface guildOptions extends BasicOptions {
  type: 'guild'
  // 分桐id
  guildId: number
}

type Options = HomewrokOptions | DairyOptions | guildOptions

@Observer
@Component({
  components: {
    EditorContent,
    ImageUploader,
    Draggable: draggable
  }
})
export default class UserContentEditor extends Vue {
  // 当前的用户内容提交类型
  @Prop({ required: true }) options: Options

  @Ref()
  uploader: { chooseImage: () => void; removeUploadTask: (uid: string) => void; reloadUploadTask: (uid: string) => void } | null

  @Ref()
  editorContent?: { getFormStatus: () => { hasTitle: boolean; hasContent: boolean } }

  // 如果当前编辑类型为作业 本字段有效
  uploaderType: 'description' | 'image-preview' = 'description'

  imageList: Array<Omit<UploadItemType, 'rawImageFile'>> = []

  activeIndex = 0

  drag = false

  isSubmitting = false

  // export
  getFormStatus() {
    const { hasContent = false, hasTitle = false } = this.editorContent?.getFormStatus() || {}

    return {
      hasImage: this.imageList.length !== 0,
      hasContent,
      hasTitle
    }
  }

  get PageStore() {
    return this.$store.$courseV2
  }

  get isAllImageUploadSuccess() {
    return this.imageList.every(i => i.status === 'success')
  }

  handleSwitchDescriptionTab() {
    this.uploaderType = 'description'
  }

  get submitDisabled() {
    if (this.options.type === 'dairy' || this.options.type === 'guild') {
      return !this.isAllImageUploadSuccess || this.imageList.length === 0
    }
    return !this.isAllImageUploadSuccess || (this.options.imageRequired && this.imageList.length === 0)
  }

  handleSwitchImagePreviewTab(imageIndex: number, uid: string) {
    this.uploaderType = 'image-preview'

    this.activeIndex = imageIndex
  }

  handleRemove(uid: string) {
    this.uploader?.removeUploadTask(uid)
  }

  handleReload(uid: string) {
    this.uploader?.reloadUploadTask(uid)
  }

  handleSelectImage() {
    this.uploader?.chooseImage()
  }

  handleImageListChange(imageList: Array<Omit<UploadItemType, 'rawImageFile'>>) {
    this.imageList = imageList

    // 后续图片上传自动切换到对应的图片展示
    if (imageList.length > 1 && this.uploaderType === 'image-preview') {
      const index = imageList.length - 1
      this.activeIndex = index
    }

    if (imageList.length && imageList[0].status === 'success' && this.uploaderType === 'description') {
      this.uploaderType = 'image-preview'
    }
  }

  async handleSubmit(payload: { title: string; content: string; isPublic: boolean }) {
    if (!this.isAllImageUploadSuccess) return

    if (this.options.type === 'homework') {
      // 提交防御下沉
      if (this.options.contentRequired && payload.content.trim().length === 0) {
        this.$message.error('请填写内容')
        return
      }

      if (this.options.imageRequired && this.imageList.length === 0) {
        this.$message.error('请上传图片')
        return
      }

      this.isSubmitting = true

      const success = await this.PageStore.submitHomework(this.options.taskId, {
        content: payload.content,
        isPublic: payload.isPublic,
        title: payload.title,
        images: (this.imageList as UploadSuccessItem[]).map(i => {
          return {
            waterUrl: i.waterUrl,
            originalHeight: i.originalHeight,
            originalWidth: i.originalWidth,
            originUrl: i.originUrl
          }
        })
      })

      if (success) {
        this.$emit('reload')
        this.$message.success('发布成功')
      }
    } else if (this.options.type === 'dairy') {
      if (payload.content.trim().length === 0 && this.imageList.length === 0) {
        this.$message.error('请填写内容')
        return
      }

      this.isSubmitting = true

      const newDairy = await this.PageStore.submitDairy(this.options.courseId, {
        content: payload.content,
        isPublic: payload.isPublic,
        title: payload.title,
        images: (this.imageList as UploadSuccessItem[]).map(i => {
          return {
            waterUrl: i.waterUrl,
            originalHeight: i.originalHeight,
            originalWidth: i.originalWidth,
            originUrl: i.originUrl
          }
        })
      })

      if (newDairy) {
        this.$emit('reload', { payload: newDairy })
        this.$message.success('发布成功')
      }
    } else if (this.options.type === 'guild') {
      if (payload.content.trim().length === 0 && this.imageList.length === 0) {
        this.$message.error('请填写内容')
        return
      }

      this.isSubmitting = true

      const newDairy = await this.PageStore.submitDairyV2(this.options.guildId, {
        content: payload.content,
        isPublic: payload.isPublic,
        title: payload.title,
        images: (this.imageList as UploadSuccessItem[]).map(i => {
          return {
            waterUrl: i.waterUrl,
            originalHeight: i.originalHeight,
            originalWidth: i.originalWidth,
            originUrl: i.originUrl
          }
        })
      })

      if (newDairy) {
        this.$emit('reload', { payload: newDairy })
        this.$message.success('发布成功')
      }
    }

    this.isSubmitting = false
  }

  created() {}
  mounted() {}
  updated() {}
  destroyed() {}
}
